import { SdkContext } from '../internal'
import type { MemberBadge } from '@/types/badges'

/** F-148: the ranking's badges are the person's public ones (`{ id, name, imageUrl }`; before, `{ icon, color }`). */
export interface LeaderboardBadge {
  id: string
  name: string
  imageUrl: string
  description?: string | null
  visibility?: 'public' | 'private'
  /** The grant, as on the profile (same popover): since 2026-10-30. */
  grantId?: string
  source?: MemberBadge['source']
  sourceRef?: string | null
  origin?: MemberBadge['origin']
  awardedAt?: string
}

export interface LeaderboardEntry {
  /** Competition ranking: tied points share the position (1, 2, 2, 2, 5). */
  rank: number
  /** Another row has the same points. */
  isTied?: boolean
  /** Position in the window before (7d, 30d); null with `all` or without points then. */
  previousRank?: number | null
  /** The stored level's number in the community's ladder (1 = first). */
  level?: number
  /** F-152: the level's name (the default one in the reader's language) and image. */
  levelId?: string | null
  levelName?: string | null
  levelImageUrl?: string | null
  /** All the badges the viewer may see; `badges` carries at most 8 (newest first). */
  badgeCount?: number
  medal?: string
  memberId: string
  /** U7: null (before, "Unknown") for a person with no name at all; show it with `personName`. */
  fullName: string | null
  displayName?: string | null
  role: string
  avatarUrl?: string | null
  totalPoints: number
  badges: LeaderboardBadge[]
  isTopThree: boolean
}

export interface UserScore {
  totalPoints: number
  level: number
  rank: number
  progressPercentage: number
  pointsToNextLevel: number
  memberId?: string
  /** The viewer's position and points in the requested period, also outside the rows; rank null without points. */
  periodRank?: number | null
  periodPoints?: number
  /** The viewer's own badges (private ones included), newest first, up to 12; `badgeCount` is the total. */
  badges?: LeaderboardBadge[]
  badgeCount?: number
  /** F-152: the stored level's name and image, and the next level's name (null at the top). */
  levelName?: string | null
  levelImageUrl?: string | null
  nextLevelName?: string | null
  [key: string]: unknown
}

export interface LeaderboardResponse {
  period: string
  sectionId: string | null
  leaderboard: LeaderboardEntry[]
}

export interface GamificationConfig {
  isEnabled: boolean
  actionRules?: Record<string, number>
  [key: string]: unknown
}

export interface PointsRule {
  actionType: string
  points: number
  isActive: boolean
  [key: string]: unknown
}

export interface LevelDistribution {
  level: number
  count?: number
  percentage: number
  id?: string | null
  name?: string
  imageUrl?: string | null
  minPoints?: number
  [key: string]: unknown
}

export interface GamificationData {
  leaderboard: LeaderboardEntry[]
  currentUser?: UserScore | null
  pointsRules?: PointsRule[]
  levelDistribution?: LevelDistribution[]
  /** F-152: the community's whole ladder. */
  levels?: GamificationLevel[]
  [key: string]: unknown
}

/** F-152: a weekly activity requirement of a level ("at least 3 posts in the week", optionally in one space). */
export interface LevelRequirement {
  actionType: string
  minCount: number
  spaceId: string | null
  /** False when the action is off in the points rules (it is not counting). */
  actionActive?: boolean
}

/** F-152: a level of the community's ladder. */
export interface GamificationLevel {
  id: string | null
  /** 1 = the first level. */
  level: number
  name: string
  /** The name is still the default one, translated. */
  defaultName?: boolean
  minPoints: number
  maxPoints: number | null
  imageUrl: string | null
  requirements: LevelRequirement[]
}

/** `GET /api/gamification/levels` (owner/admin): the ladder plus what the editor needs. */
export interface LevelsEditorData {
  levels: GamificationLevel[]
  limits: { min: number; max: number; name: number; requirements: number; minCount: number; maxCount: number }
  eligibleActions: Array<{ actionType: string; active: boolean; inSpace: boolean }>
  timeZone: string
  /** The rule in the reader's language. */
  rule: string
  /** On `PUT`: how many people the change moves (`people` evaluated, `up`, `down`). */
  changes?: { people: number; up: number; down: number }
  dryRun?: boolean
}

export interface LevelsInput {
  dryRun?: boolean
  levels: Array<{
    id?: string
    name: string
    minPoints: number
    imageUrl: string | null
    requirements: Array<{ actionType: string; minCount: number; spaceId: string | null }>
  }>
}

/** F-152 criterion 22: a requirement with what the person did this week and the last one. */
export interface LevelProgressRequirement {
  actionType: string
  spaceId: string | null
  spaceName: string | null
  /** SEC-309: the requirement's space exists but the viewer cannot open it; `spaceId` and `spaceName` come null. */
  spaceHidden?: boolean
  minCount: number
  thisWeek: number
  lastWeek: number
}

export interface LevelProgressLevel {
  level: number
  id: string | null
  name: string
  minPoints: number
  requirements: LevelProgressRequirement[]
}

/** `GET /api/gamification/levels/progress` and `engagement.levelProgress` on the profile. */
export interface LevelProgress {
  points: number
  level: LevelProgressLevel | null
  next: LevelProgressLevel | null
  week: { startsAt: string; timeZone: string }
}

/** `POST /api/gamification/points`: `skipped` when the gamification is off. */
export interface PointsAdjustment {
  skipped?: boolean
  totalPoints?: number
  [key: string]: unknown
}

export class GamificationResource {
  constructor(private ctx: SdkContext) {}

  async getAll(params?: { period?: string; sectionId?: string; limit?: number }): Promise<GamificationData> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/gamification' }, { query: params })
  }

  async getLeaderboard(params?: { period?: string; sectionId?: string; limit?: number }): Promise<LeaderboardResponse> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/gamification/leaderboard' }, { query: params })
  }

  async getUserScore(sectionId?: string): Promise<UserScore> {
    return this.ctx.raw(
      { httpMethod: 'GET', path: '/gamification/score' },
      sectionId ? { query: { sectionId } } : {}
    )
  }

  async getUserActions(limit?: number) {
    return this.ctx.raw(
      { httpMethod: 'GET', path: '/gamification/actions' },
      limit ? { query: { limit } } : {}
    )
  }

  async getConfig(): Promise<GamificationConfig> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/gamification/config' })
  }

  /**
   * Owner/admin. `actionRules` maps an action type to its points
   * (`{ CREATE_POST: 10 }`); types left out keep theirs.
   */
  async updateConfig(body: { isEnabled?: boolean; actionRules?: Record<string, number> }) {
    return this.ctx.raw({ httpMethod: 'PUT', path: '/gamification/config', hasBody: true }, { body })
  }

  /** F-152: the community's levels and requirements (owner/admin). The first read stores the 9 default ones. */
  async getLevels(): Promise<LevelsEditorData> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/gamification/levels' })
  }

  /**
   * F-152: replaces the whole ladder (owner/admin): a level with `id` is kept, one without is created, one
   * left out is removed (409 when an access rule uses it). 422 per field (`levels[2].minPoints`). With
   * `dryRun`, nothing is saved and `changes` says how many people would move.
   */
  async saveLevels(body: LevelsInput): Promise<LevelsEditorData> {
    return this.ctx.raw({ httpMethod: 'PUT', path: '/gamification/levels', hasBody: true }, { body })
  }

  /** F-152: the week's progress of a person (own, or anyone's for owner/admin/moderator; 403 otherwise). */
  async getLevelProgress(memberId?: string): Promise<LevelProgress> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/gamification/levels/progress' }, memberId ? { query: { memberId } } : {})
  }

  /**
   * F-153 "Ajustar pontos" (owner and admin): `points` adds (or, negative,
   * takes away), never 0, from -100,000 to 100,000; `reason` (1 to 200
   * characters) goes to the person's points history.
   */
  async adjustPoints(body: { memberId: string; points: number; reason: string }): Promise<PointsAdjustment> {
    return this.ctx.raw({ httpMethod: 'POST', path: '/gamification/points', hasBody: true }, { body })
  }
}
